import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Button } from 'components/button';
import { IoClose, IoCheckmark, IoList, IoDownload, IoTrash, IoRefresh } from 'react-icons/io5';
import {
  getReportJobs,
  downloadReconciliationReportFile,
  deleteReportFile,
  deleteAllReportFiles,
  createReconciliationReportJob,
  DateRange,
  ReportFormat
} from 'services/reconciliationService';

export interface ColumnOption {
  id: string;
  label: string;
  description?: string;
  category: 'basic' | 'financial' | 'qbo' | 'location' | 'billing' | 'service' | 'metadata';
  defaultSelected: boolean;
}

export interface ColumnSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: DateRange;
  format: ReportFormat;
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

type TabType = 'columns' | 'jobs';

interface ReportJob {
  jobId: number;
  status: string;
  fileBytes?: number;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  fileExists?: boolean;
  fileName?: string;
  error?: string;
  params: {
    startDate: string;
    endDate: string;
    format: string;
    columns: string[];
  };
}

const AVAILABLE_COLUMNS: ColumnOption[] = [
  // Basic Invoice Information (Working columns first)
  { id: 'invoice_id', label: 'Invoice ID', description: 'EServ invoice identifier', category: 'basic', defaultSelected: true },
  { id: 'invoice_number', label: 'DocNumber', description: 'Customer-facing invoice number', category: 'basic', defaultSelected: true },
  { id: 'applied_payment_ids', label: 'Applied Payment IDs', description: 'Comma-separated list of payment IDs applied to this invoice', category: 'basic', defaultSelected: true },
  { id: 'customer_id', label: 'Customer ID', description: 'Customer ID from payment data', category: 'basic', defaultSelected: true },
  { id: 'customer_name', label: 'Customer Name', description: 'Customer first and last name', category: 'basic', defaultSelected: true },
  { id: 'company_name', label: 'Company Name', description: 'Customer company name', category: 'basic', defaultSelected: false },
  { id: 'invoice_date', label: 'Invoice Date', description: 'Date invoice was created', category: 'basic', defaultSelected: true },
  { id: 'status', label: 'Status', description: 'Invoice status (paid, pending, etc.)', category: 'basic', defaultSelected: true },
  { id: 'eserv_grand_total', label: 'EServ Grand Total', description: 'Final total after all calculations', category: 'financial', defaultSelected: true },
  { id: 'paid_amount', label: 'Total Paid (EServ)', description: 'Amount already paid', category: 'basic', defaultSelected: true },
  { id: 'balance_due', label: 'Balance Due (EServ)', description: 'Remaining amount to be paid', category: 'basic', defaultSelected: true },
  { id: 'eserv_subtotal', label: 'EServ Subtotal', description: 'Line items subtotal before taxes/fees/discounts', category: 'financial', defaultSelected: false },
  { id: 'eserv_taxes', label: 'EServ Taxes', description: 'Total tax amount applied to invoice', category: 'financial', defaultSelected: false },
  { id: 'eserv_fees', label: 'EServ Fees', description: 'Total fee amount applied to invoice', category: 'financial', defaultSelected: false },
  { id: 'eserv_discounts', label: 'EServ Discounts', description: 'Total discount amount applied to invoice', category: 'financial', defaultSelected: false },
  { id: 'qbo_invoice_total_due', label: 'QBO Total Due', description: 'Balance due reported by QuickBooks Online', category: 'qbo', defaultSelected: false },
  { id: 'qbo_payment_total', label: 'QBO Payment Total', description: 'Total payments applied in QuickBooks Online', category: 'qbo', defaultSelected: false },
  { id: 'payment_mismatch_amount', label: 'Payment Difference (EServ - QBO)', description: 'Difference between EServ payments and QBO payments', category: 'financial', defaultSelected: false },
  { id: 'payment_mismatch_flag', label: 'Payment Mismatch Flag', description: 'Flagged when EServ and QBO payments differ by more than $0.01', category: 'financial', defaultSelected: false },
  { id: 'total_due_difference', label: 'Total Due Difference (EServ - QBO)', description: 'Difference between balances due in EServ and QBO', category: 'financial', defaultSelected: false },
  { id: 'total_amount_difference', label: 'Total Amount Difference (EServ - QBO)', description: 'Difference between invoice totals in EServ and QBO', category: 'financial', defaultSelected: false },
  { id: 'qbo_invoice_id', label: 'QBO Invoice ID', description: 'QuickBooks Online invoice ID', category: 'qbo', defaultSelected: true },
  { id: 'qbo_customer_id', label: 'QBO Customer ID', description: 'QuickBooks Online customer ID', category: 'qbo', defaultSelected: true },
  { id: 'invoice_total_qbo', label: 'Invoice Total (QBO)', description: 'Total invoice amount from QuickBooks Online', category: 'financial', defaultSelected: true },
  { id: 'qbo_subtotal', label: 'QBO Subtotal', description: 'Line items subtotal before taxes/fees/discounts from QBO', category: 'financial', defaultSelected: false },
  { id: 'qbo_taxes', label: 'QBO Taxes', description: 'Total tax amount from QuickBooks Online', category: 'financial', defaultSelected: false },
  { id: 'qbo_fees', label: 'QBO Fees', description: 'Total fee amount from QuickBooks Online', category: 'financial', defaultSelected: false },
  { id: 'qbo_discounts', label: 'QBO Discounts', description: 'Total discount amount from QuickBooks Online', category: 'financial', defaultSelected: false },
  { id: 'qbo_payment_ids', label: 'QBO Payment IDs', description: 'Payment IDs from QuickBooks Online (comma separated)', category: 'financial', defaultSelected: false },
  { id: 'qbo_payment_amounts', label: 'QBO Payment Amounts', description: 'Payment amounts from QuickBooks Online (comma separated)', category: 'financial', defaultSelected: false },
  { id: 'qbo_id', label: 'QBO ID', description: 'QuickBooks Online invoice ID', category: 'basic', defaultSelected: true },
  { id: 'qbo_sync_date', label: 'QBO Sync Date', description: 'When invoice was synced to QBO', category: 'basic', defaultSelected: false },
  { id: 'qbo_status', label: 'QBO Status', description: 'Status in QuickBooks Online', category: 'basic', defaultSelected: false },
  
  // Additional Information
  { id: 'due_date', label: 'Due Date', description: 'Payment due date from invoice', category: 'basic', defaultSelected: false },
  // { id: 'request_id', label: 'Request ID', description: 'Associated service request identifier', category: 'basic', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'project_id', label: 'Project ID', description: 'Associated project identifier', category: 'basic', defaultSelected: false }, // COMMENTED OUT - not verified
  
  // Financial Information
  // { id: 'subtotal', label: 'Subtotal', description: 'Invoice subtotal before taxes', category: 'financial', defaultSelected: true }, // COMMENTED OUT - not verified
  // { id: 'tax_amount', label: 'Tax Amount', description: 'Total tax amount', category: 'financial', defaultSelected: true }, // COMMENTED OUT - not verified
  // { id: 'total_amount', label: 'Total Amount', description: 'Final invoice total', category: 'financial', defaultSelected: true }, // COMMENTED OUT - not verified
  // { id: 'list_tax_total', label: 'List Tax Total', description: 'Tax amount based on list price', category: 'financial', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'sale_tax_total', label: 'Sale Tax Total', description: 'Tax amount based on sale price', category: 'financial', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'install_tax_total', label: 'Install Tax Total', description: 'Tax amount for installation services', category: 'financial', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'amount_paid', label: 'Amount Paid', description: 'Total amount received from customer', category: 'financial', defaultSelected: true }, // COMMENTED OUT - not verified
  // { id: 'amount_due', label: 'Amount Due', description: 'Outstanding balance on invoice', category: 'financial', defaultSelected: true }, // COMMENTED OUT - not verified
  
  // QuickBooks Desktop Information
  // { id: 'quickbooks_desktop_sync_status', label: 'QB Desktop Sync Status', description: 'Sync status with QuickBooks Desktop', category: 'qbo', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'desktop_synced_date_time', label: 'Desktop Synced Date/Time', description: 'When invoice was synced to QB Desktop', category: 'qbo', defaultSelected: false }, // COMMENTED OUT - not verified
  
  // QuickBooks Online Information
  // { id: 'qbo_sync_date', label: 'QBO Sync Date', description: 'When invoice was synced to QBO', category: 'qbo', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'qbo_status', label: 'QBO Status', description: 'Status in QuickBooks Online', category: 'qbo', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'qbo_customer_id', label: 'QBO Customer ID', description: 'Customer ID in QuickBooks Online', category: 'qbo', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'quickbooks_online_sync_status', label: 'QB Online Sync Status', description: 'Sync status with QuickBooks Online', category: 'qbo', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'online_synced_date_time', label: 'Online Synced Date/Time', description: 'When invoice was synced to QB Online', category: 'qbo', defaultSelected: false }, // COMMENTED OUT - not verified
  
  // Service Location Information
  // { id: 'service_location_address', label: 'Service Location Address', description: 'Address where service was performed', category: 'location', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'service_location_city', label: 'Service Location City', description: 'City of service location', category: 'location', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'service_location_state', label: 'Service Location State', description: 'State of service location', category: 'location', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'service_location_zipcode', label: 'Service Location Zipcode', description: 'ZIP code of service location', category: 'location', defaultSelected: false }, // COMMENTED OUT - not verified
  
  // Customer Billing Information
  // { id: 'bill_to_customer_name', label: 'Bill To Customer Name', description: 'Name of customer being billed', category: 'billing', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'bill_to_customer_address', label: 'Bill To Customer Address', description: 'Billing address for customer', category: 'billing', defaultSelected: false }, // COMMENTED OUT - not verified
  
  // Service Details
  // { id: 'custom_document', label: 'Custom Document', description: 'Custom document or attachment', category: 'service', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'completion_notes', label: 'Completion Notes', description: 'Notes about service completion', category: 'service', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'appointment_ids', label: 'Appointment IDs', description: 'Associated appointment identifiers', category: 'service', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'appointment_techs', label: 'Appointment Techs', description: 'Technicians assigned to appointments', category: 'service', defaultSelected: false }, // COMMENTED OUT - not verified
  
  // Metadata
  // { id: 'updated_at', label: 'Updated At', description: 'Last modification date', category: 'metadata', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'created_by', label: 'Created By', description: 'User who created the invoice', category: 'metadata', defaultSelected: false }, // COMMENTED OUT - not verified
  // { id: 'notes', label: 'Notes', description: 'Invoice notes or comments', category: 'metadata', defaultSelected: false }, // COMMENTED OUT - not verified
];

const CATEGORIES = [
  { id: 'basic', label: 'Basic Information', color: '#3B82F6' },
  { id: 'financial', label: 'Financial Data', color: '#10B981' },
  { id: 'qbo', label: 'QuickBooks Integration', color: '#F59E0B' },
  { id: 'location', label: 'Service Location', color: '#8B5CF6' },
  { id: 'billing', label: 'Billing Information', color: '#EF4444' },
  { id: 'service', label: 'Service Details', color: '#06B6D4' },
  { id: 'metadata', label: 'Metadata', color: '#6B7280' },
];

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #6b7280;
  
  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }
`;

// Tab Components
const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
`;

const TabButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#111827' : '#6b7280'};
  font-size: 14px;
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'white' : '#f3f4f6'};
    color: ${props => props.active ? '#111827' : '#374151'};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const Description = styled.p`
  margin: 0 0 20px 0;
  color: #6b7280;
  font-size: 14px;
`;

const SelectedSummary = styled.div`
  margin: 0 0 20px 0;
  padding: 12px 16px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  
  .selected-preview {
    display: block;
    margin-top: 4px;
    color: #64748b;
    font-size: 13px;
    font-style: italic;
  }
`;

const BulkActions = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const ColumnGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CategorySection = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const CategoryTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

const CategoryToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #6b7280;
  
  &:hover {
    background-color: #e5e7eb;
  }
  
  &.all-selected {
    color: #059669;
    font-weight: 500;
  }
  
  &.some-selected {
    color: #d97706;
    font-weight: 500;
  }
`;

const CategoryColumns = styled.div`
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
`;

const ColumnOptionItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #f9fafb;
  }
`;

const ColumnCheckbox = styled.input`
  margin: 0;
  margin-top: 2px;
`;

const ColumnInfo = styled.div`
  flex: 1;
`;

const ColumnLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 2px;
`;

const ColumnDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

// Jobs Tab Components
const JobsTabContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const JobsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
`;

const JobsTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const JobsHeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const JobsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
`;

const JobItem = styled.div<{ highlighted?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid ${props => (props.highlighted ? '#3b82f6' : '#e5e7eb')};
  border-radius: 8px;
  background: ${props => (props.highlighted ? '#f0f7ff' : 'white')};
  transition: box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
    border-color: ${props => (props.highlighted ? '#2563eb' : '#d1d5db')};
  }
`;

const JobInfo = styled.div`
  flex: 1;
  margin-right: 16px;
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const JobId = styled.span`
  font-weight: 600;
  color: #111827;
  font-size: 14px;
`;

const JobStatus = styled.span`
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const JobDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  
  div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const JobActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 16px;
  border: 1px solid #fca5a5;
`;

const LoadingMessage = styled.div`
  background: #dbeafe;
  color: #1d4ed8;
  padding: 20px;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  border: 1px solid #93c5fd;
`;

const EmptyMessage = styled.div`
  background: #f9fafb;
  color: #6b7280;
  padding: 40px 20px;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  border: 1px solid #e5e7eb;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
`;

const ColumnSelectionModal: React.FC<ColumnSelectionModalProps> = ({
  isOpen,
  onClose,
  dateRange,
  format,
  onStart,
  onComplete,
  onError
}) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    AVAILABLE_COLUMNS.filter(col => col.defaultSelected).map(col => col.id) || []
  );
  const [activeTab, setActiveTab] = useState<TabType>('columns');
  const [jobs, setJobs] = useState<ReportJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastCreatedJobId, setLastCreatedJobId] = useState<number | null>(null);
  const [highlightedJobId, setHighlightedJobId] = useState<number | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);

  const loadJobs = useCallback(
    async (withSpinner: boolean = true) => {
      if (withSpinner) {
        setJobsLoading(true);
      }

      try {
        const response = await getReportJobs();
        setJobs(response.jobs || []);
        setJobsError(null);
      } catch (error) {
        console.error('Failed to load jobs:', error);
        setJobsError(error instanceof Error ? error.message : 'Failed to load jobs');
      } finally {
        if (withSpinner) {
          setJobsLoading(false);
        }
      }
    },
    []
  );
  // Load jobs and poll while downloads tab is active - poll every 5s for 30s, then stop
  useEffect(() => {
    if (isOpen && activeTab === 'jobs') {
      loadJobs();
      if (!pollingRef.current) {
        pollingStartTimeRef.current = Date.now();
        pollingRef.current = setInterval(() => {
          // Stop polling after 30 seconds
          if (pollingStartTimeRef.current && Date.now() - pollingStartTimeRef.current > 30000) {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              pollingStartTimeRef.current = null;
            }
            return;
          }
          
          loadJobs(false).catch(() => {
            // error is handled inside loadJobs
          });
        }, 5000); // Poll every 5 seconds instead of 3
      }
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      pollingStartTimeRef.current = null;
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        pollingStartTimeRef.current = null;
      }
    };
  }, [isOpen, activeTab, loadJobs]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('columns');
      setIsGenerating(false);
      setLastCreatedJobId(null);
      setHighlightedJobId(null);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      pollingStartTimeRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!lastCreatedJobId) {
      return;
    }

    const job = jobs.find(j => j.jobId === lastCreatedJobId);
    if (!job) {
      return;
    }

    if (job.status === 'failed') {
      const message = job.error || 'Report generation failed';
      setJobsError(message);
      onError?.(new Error(message));
      setLastCreatedJobId(null);
      return;
    }

    if (job.status === 'ready' || job.status === 'completed') {
      onComplete?.();
      setLastCreatedJobId(null);
    }
  }, [jobs, lastCreatedJobId, onComplete, onError]);



  const handleDownloadJob = async (job: ReportJob) => {
    if (job.status !== 'ready') return;
    
    try {
      const filename = `qbo-reconciliation-${job.params.startDate}-${job.params.endDate}.${job.params.format}`;
      await downloadReconciliationReportFile(job.jobId, filename);
    } catch (error) {
      console.error('Failed to download job:', error);
      setJobsError(error instanceof Error ? error.message : 'Download failed');
    }
  };

  const handleDeleteJob = async (job: ReportJob) => {
    if (!window.confirm(`Are you sure you want to delete this report? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await deleteReportFile(job.jobId);
      await loadJobs(); // Refresh the list
    } catch (error) {
      console.error('Failed to delete job:', error);
      setJobsError(error instanceof Error ? error.message : 'Delete failed');
    }
  };

  const handleDeleteAllJobs = async () => {
    if (!window.confirm(`Are you sure you want to delete ALL report files? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const result = await deleteAllReportFiles();
      await loadJobs(); // Refresh the list
      
      if (result.errors && result.errors.length > 0) {
        setJobsError(`Deleted ${result.deletedCount} files, but ${result.errors.length} failed: ${result.errors.map(e => e.fileName).join(', ')}`);
      }
    } catch (error) {
      console.error('Failed to delete all jobs:', error);
      setJobsError(error instanceof Error ? error.message : 'Delete all failed');
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready': return '#10B981';
      case 'running': return '#F59E0B';
      case 'queued': return '#3B82F6';
      case 'failed': return '#EF4444';
      case 'no_file': return '#F97316';
      default: return '#6B7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'no_file': return 'NO FILE';
      default: return status.toUpperCase();
    }
  };

  const handleColumnToggle = (columnId: string) => {
    setSelectedColumns(prev => 
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    setSelectedColumns(AVAILABLE_COLUMNS.map(col => col.id));
  };

  const handleSelectNone = () => {
    setSelectedColumns([]);
  };

  const handleSelectRecommended = () => {
    // Select columns that are most commonly needed for reconciliation reports
    // Only include verified fields
    const recommendedColumns = [
  'invoice_id', 'invoice_number', 'applied_payment_ids', 'customer_id', 'customer_name', 'company_name', 'invoice_date',
  'due_date', 'status', 'eserv_grand_total', 'paid_amount', 'balance_due', 'payment_ids', 'invoice_total_eserv', 'eserv_subtotal', 'eserv_taxes', 'eserv_fees', 'eserv_discounts', 'invoice_total_qbo', 'qbo_invoice_total_due', 'total_amount_difference', 'total_due_difference', 'payment_mismatch_amount', 'qbo_payment_total', 'qbo_subtotal', 'qbo_taxes', 'qbo_fees', 'qbo_discounts', 'qbo_payment_ids', 'qbo_payment_amounts', 'qbo_id', 'qbo_sync_date', 'qbo_status'
    ];
    setSelectedColumns(recommendedColumns);
  };

  const handleSelectCategory = (categoryId: string) => {
    const categoryColumns = AVAILABLE_COLUMNS
      .filter(col => col.category === categoryId)
      .map(col => col.id);
    
    const allCategorySelected = categoryColumns.every(col => selectedColumns?.includes(col));
    
    if (allCategorySelected) {
      // Deselect all in category
      setSelectedColumns(prev => prev?.filter(col => !categoryColumns.includes(col)) || []);
    } else {
      // Select all in category
      setSelectedColumns(prev => [...new Set([...(prev || []), ...categoryColumns])]);
    }
  };

  const handleConfirm = async () => {
    if (!selectedColumns || selectedColumns.length === 0) {
      alert('Please select at least one column for the report.');
      return;
    }

    if (isGenerating) {
      return;
    }

    try {
      setIsGenerating(true);
      setJobsError(null);
      onStart?.();

      const job = await createReconciliationReportJob(dateRange, format, selectedColumns);
      setLastCreatedJobId(job.jobId);
      setHighlightedJobId(job.jobId);
      setActiveTab('jobs');
      await loadJobs(false);
      
      // Restart polling timer when a new job is created
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      pollingStartTimeRef.current = Date.now();
      pollingRef.current = setInterval(() => {
        // Stop polling after 30 seconds
        if (pollingStartTimeRef.current && Date.now() - pollingStartTimeRef.current > 30000) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            pollingStartTimeRef.current = null;
          }
          return;
        }
        
        loadJobs(false).catch(() => {
          // error is handled inside loadJobs
        });
      }, 5000);
    } catch (error) {
      console.error('Failed to create report job:', error);
      const message = error instanceof Error ? error.message : 'Failed to create report job';
      setJobsError(message);
      onError?.(error instanceof Error ? error : new Error(message));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRefreshJobs = () => {
    // Restart polling timer when user manually refreshes
    loadJobs();
    
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    pollingStartTimeRef.current = Date.now();
    pollingRef.current = setInterval(() => {
      // Stop polling after 30 seconds
      if (pollingStartTimeRef.current && Date.now() - pollingStartTimeRef.current > 30000) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          pollingStartTimeRef.current = null;
        }
        return;
      }
      
      loadJobs(false).catch(() => {
        // error is handled inside loadJobs
      });
    }, 5000);
  };

  const handleModalClose = () => {
    if (isGenerating) {
      return;
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleModalClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Report Builder</ModalTitle>
          <CloseButton onClick={handleModalClose} disabled={isGenerating}>
            <IoClose size={20} />
          </CloseButton>
        </ModalHeader>

        <TabContainer>
          <TabButton 
            active={activeTab === 'columns'} 
            onClick={() => !isGenerating && setActiveTab('columns')}
          >
            <IoCheckmark size={16} />
            Column Selection
          </TabButton>
          <TabButton 
            active={activeTab === 'jobs'} 
            onClick={() => setActiveTab('jobs')}
          >
            <IoList size={16} />
            Report Jobs
          </TabButton>
        </TabContainer>

        <ModalBody>
          {activeTab === 'columns' ? (
            <>
              <Description>
                Choose which columns to include in your reconciliation report. 
                At least one column must be selected.
                <br />
                <strong>New:</strong> Enhanced columns for comprehensive reconciliation including service locations, 
                billing details, and QuickBooks Desktop/Online sync status.
              </Description>

              <SelectedSummary>
                <strong>Selected: {selectedColumns?.length || 0} of {AVAILABLE_COLUMNS.length} columns</strong>
                {selectedColumns && selectedColumns.length > 0 && (
                  <span className="selected-preview">
                    {selectedColumns.slice(0, 3).map(id => {
                      const col = AVAILABLE_COLUMNS.find(c => c.id === id);
                      return col ? col.label : id;
                    }).join(', ')}
                    {selectedColumns.length > 3 && ` +${selectedColumns.length - 3} more`}
                  </span>
                )}
              </SelectedSummary>

              <BulkActions>
                <Button size="small" onClick={handleSelectAll}>Select All</Button>
                <Button size="small" onClick={handleSelectNone}>Select None</Button>
                <Button size="small" onClick={handleSelectRecommended}>Select Recommended</Button>
              </BulkActions>

              <ColumnGrid>
                {CATEGORIES.map(category => {
                  const categoryColumns = AVAILABLE_COLUMNS.filter(col => col.category === category.id);
                  const selectedInCategory = categoryColumns.filter(col => selectedColumns?.includes(col.id));
                  const allSelected = categoryColumns.length > 0 && selectedInCategory.length === categoryColumns.length;
                  const someSelected = selectedInCategory.length > 0 && !allSelected;

                  return (
                    <CategorySection key={category.id}>
                      <CategoryHeader>
                        <CategoryTitle style={{ color: category.color }}>
                          {category.label}
                        </CategoryTitle>
                        <CategoryToggle
                          onClick={() => handleSelectCategory(category.id)}
                          className={allSelected ? 'all-selected' : someSelected ? 'some-selected' : ''}
                        >
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </CategoryToggle>
                      </CategoryHeader>
                      
                      <CategoryColumns>
                        {categoryColumns.map(column => (
                          <ColumnOptionItem key={column.id}>
                            <ColumnCheckbox
                              type="checkbox"
                              checked={selectedColumns?.includes(column.id) || false}
                              onChange={() => handleColumnToggle(column.id)}
                            />
                            <ColumnInfo>
                              <ColumnLabel>{column.label}</ColumnLabel>
                              {column.description && (
                                <ColumnDescription>{column.description}</ColumnDescription>
                              )}
                            </ColumnInfo>
                          </ColumnOptionItem>
                        ))}
                      </CategoryColumns>
                    </CategorySection>
                  );
                })}
              </ColumnGrid>
            </>
          ) : (
            <JobsTabContent>
              <JobsHeader>
                <JobsTitle>Report Jobs</JobsTitle>
                <JobsHeaderActions>
                  <Button size="small" onClick={handleRefreshJobs} disabled={jobsLoading || isGenerating}>
                    <IoRefresh size={14} />
                    Refresh
                  </Button>
                  {jobs.length > 0 && (
                    <Button 
                      size="small" 
                      onClick={() => handleDeleteAllJobs()}
                      disabled={jobsLoading || isGenerating}
                      style={{ backgroundColor: '#EF4444', color: 'white' }}
                    >
                      <IoTrash size={14} />
                      Delete All
                    </Button>
                  )}
                </JobsHeaderActions>
              </JobsHeader>

              {jobsError && (
                <ErrorMessage>{jobsError}</ErrorMessage>
              )}

              {jobsLoading ? (
                <LoadingMessage>Loading jobs...</LoadingMessage>
              ) : jobs.length === 0 ? (
                <EmptyMessage>No report jobs found. Create a new report to get started.</EmptyMessage>
              ) : (
                <JobsList>
                  {jobs.map(job => (
                    <JobItem key={job.jobId} highlighted={job.jobId === highlightedJobId}>
                      <JobInfo>
                        <JobHeader>
                          <JobId>Job #{job.jobId}</JobId>
                          <JobStatus style={{ color: getStatusColor(job.status) }}>
                            {getStatusLabel(job.status)}
                          </JobStatus>
                        </JobHeader>
                        <JobDetails>
                          <div><strong>Date Range:</strong> {job.params.startDate} to {job.params.endDate}</div>
                          <div><strong>Format:</strong> {job.params.format.toUpperCase()}</div>
                          <div><strong>Columns:</strong> {job.params.columns.length} selected</div>
                          {job.fileBytes && <div><strong>Size:</strong> {formatFileSize(job.fileBytes)}</div>}
                          <div><strong>Created:</strong> {formatDate(job.createdAt)}</div>
                          {job.startedAt && <div><strong>Started:</strong> {formatDate(job.startedAt)}</div>}
                          {job.finishedAt && <div><strong>Finished:</strong> {formatDate(job.finishedAt)}</div>}
                        </JobDetails>
                      </JobInfo>
                      <JobActions>
                        {(job.status === 'ready' || job.status === 'completed') && job.fileExists && (
                          <Button 
                            size="small" 
                            onClick={() => handleDownloadJob(job)}
                            title="Download Report"
                          >
                            <IoDownload size={14} />
                            Download
                          </Button>
                        )}
                        {job.status === 'no_file' && (
                          <span style={{ color: '#F97316', fontSize: '12px', fontStyle: 'italic' }}>
                            File missing
                          </span>
                        )}
                        <Button 
                          size="small" 
                          onClick={() => handleDeleteJob(job)}
                          title="Delete Report"
                          style={{ backgroundColor: '#EF4444', color: 'white' }}
                        >
                          <IoTrash size={14} />
                          Delete
                        </Button>
                      </JobActions>
                    </JobItem>
                  ))}
                </JobsList>
              )}
            </JobsTabContent>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={handleModalClose} disabled={isGenerating}>
            {activeTab === 'jobs' ? 'Close' : 'Cancel'}
          </Button>
          {activeTab === 'columns' && (
            <Button 
              primary 
              onClick={handleConfirm} 
              disabled={isGenerating || !selectedColumns || selectedColumns.length === 0}
            >
              {isGenerating ? 'Generating Report...' : `Download Report (${selectedColumns?.length || 0} columns)`}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ColumnSelectionModal; 